import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AxiosError } from 'axios';
import * as yup from 'yup';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { Formik, FormikHelpers } from 'formik';

//common components
import Sharing from '../../../../shared/components/commonui/Sharing';
import ModalBox from '../../../../shared/components/commonui/Modal/ModalBox';
import Button from '../../../../shared/components/commonui/Button';

//services 
import { toggleLoader } from '../../../../redux/actions/loader.action';
import FileDownloadService from '../../../../services/file-download-services';
import { generateSeed, getContracts, getUserGeneratedETIds } from '../../../../api/dataSharing/Contracts';
import { getDefaultOrganizations } from '../../../../api/organization/organization';

//contants

//Types
import SelectWithLabel from '../../../../shared/components/commonui/HigherOrderComponents/SelectWithLabel';
import InputWithLabel from '../../../../shared/components/commonui/HigherOrderComponents/InputWithLabel';
import LabelText from '../../../../shared/components/commonui/HigherOrderComponents/LabelText';

// Scss
import './contractTable.scss';
import Toaster from '../../../../services/toaster.services';

const ContractTable = (props: IContractTableProps) => {
    const [contractList, setContractList] = useState<any>([]);
    const { toggleLoader, handleContractSelection } = props;
    const [allORGList, setAllORGLIST] = useState<any>([]);
    const [forceUpdate, setForceUpdate] = useState<boolean>(false);
    const [currentOrgDetails, setCurrentOrgDetails] = useState<any>({});
    const fileDownloadService = new FileDownloadService();
    const [orderIndexedFields, setOrderIndexedFields] = useState([{ 'orderBy': 'signedAt', 'order': 'desc', 'isActive': false }]);
    const [currentPageNo, setCurrentPageNo] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(10);
    const [totalPage, setTotalPage] = useState<number>(1);
    const [totalRecord, setTotalRecord] = useState<number>(0);

    const tabColumnList = useMemo(() => [
        { name: "Contract Id", db: "UID" },
        { name: "Organization Id", db: "ORGId" },
        { name: "Organization Name", db: "ORGName" },
        { name: "Organization Type", db: "ORGType" }
    ], []);

    const [contractInitialValue, setContractInitialValue] = useState<any>({
        ReceiverOrgId: props?.initialSelectedOrg?.ORGId || '',
        Seed: '',
        SenderOrgId: ''
    });

    const validationSchema = yup.object().shape({
        ReceiverOrgId: yup.string().required('Receiver Organization ID is required'),
        Seed: yup.string().required('Seed is required'),
        SenderOrgId: yup.string().required('Sender Organization ID is required')
    });

    useEffect(() => {
        setContractInitialValue({
            ReceiverOrgId: props?.initialSelectedOrg?.ORGId || '',
            Seed: '',
            SenderOrgId: ''
        });
    }, [props.initialSelectedOrg]);


    // Sorting Data
    const onSortingData = useCallback((orderBy: any, order: any, data: any) => {
        if (orderBy && order && data) {
            setOrderIndexedFields(data);
        }
    }, [])

    // Previous Page
    const goToPreviousPage = useCallback((page: any) => {
        setCurrentPageNo(page);
    }, [])

    // Goto Next Page
    const goToNextPage = useCallback((page: any) => {
        setCurrentPageNo(page);
    }, [])


    // Change Page Size
    const changePageSize = useCallback((value: any) => {
        setPageSize(value);
        setCurrentPageNo(1);
    }, [])

    // IsLessThenElement
    const isLessthanTotalElement = useCallback(() => {
        if ((currentPageNo * pageSize) === (totalPage - 1))
            return true;
        else
            return false;
    }, [currentPageNo, pageSize, totalPage])

    // Share Handler
    const shareDataHandler = useCallback((contractDetails: any) => {
        handleContractSelection(contractDetails);
    }, [handleContractSelection]);

    // memoizedFunctionApprove
    const memoizedFunctionApprove = useCallback((values: any) => { shareDataHandler(values) }, [shareDataHandler]);
    // viewCredentialMemoizedApprove
    const viewCredentialMemoizedApprove = useCallback((value: any) => {
        // No-op: viewCredentialApp state not used in ContractTable
    }, []);


    // Modale Share Data



    //  Get Api Contract
    const getContract = useCallback((query, type?: string) => {
        toggleLoader(true);
        getContracts(query).then(res => {
            toggleLoader(false);
            setContractList(res.data.data);
            setTotalRecord(res.data.data?.total);
            const currentPageSize = query.pageSize;
            if (Number.parseInt(res.data.data?.total) % currentPageSize === 0) {
                setTotalPage((Number.parseInt(res.data.data?.total) / currentPageSize));
            }
            else {
                setTotalPage((Math.floor(Number.parseInt(res.data.data?.total) / currentPageSize)) + 1);
            }
        }).catch(error => {
            toggleLoader(false);
        })
    }, [toggleLoader])

    useEffect(() => {
        if (pageSize && currentPageNo) {
            const query: any = { pageNo: currentPageNo, pageSize: pageSize, IsDefaultOrg: false };
            if (props.searchTextContract) {
                query.searchKey = props.searchTextContract;
            }
            getContract(query);
        }
    }, [currentPageNo, pageSize, props.forceUpdate, props.searchTextContract, getContract]);

    // Export Data
    const exportDataJsonHandler = (values: any) => {
        // const fileName = `${currentOrgDetails.ORGName} (${values.UID}).contract` || 'contract';
        const ORGName = currentOrgDetails?.ORGName ?? '';
        const UID = values?.UID ?? ''
        const fileName = ORGName && UID ? `${ORGName} (${UID}).contract` : 'contract';
        const orgDataJson = {
            SenderOrgId: values.SenderOrgId,
            ReceiverOrgId: values.ReceiverOrgId,
            Seed: values.Seed,
            UID: values.UID,
            SenderOrgName: currentOrgDetails.ORGName
        }
        fileDownloadService.objectToJsonFile(orgDataJson, fileName);
    }

    const initializeComponent = useCallback(() => {
        toggleLoader(true);
        Promise.all([getCurrentOrganizationDetails(), getContracts({ IsDefaultOrg: false }), getUserGeneratedETIds({}), getDefaultOrganizations({ "IsDefaultOrg": false })]).then((responseList: any) => {
            if (responseList.length > 0 && responseList[0].data?.data?.length > 0) {
                setCurrentOrgDetails(responseList[0].data.data[0]);
                setContractInitialValue((prevValue: any) => {
                    return {
                        ...prevValue,
                        "senderOrganizationName": responseList[0].data.data[0]?.ORGName,
                        "SenderOrgId": responseList[0].data.data[0]?.ORGId,
                    }
                });
                // setUserGeneratedETIds not used in ContractTable
                setAllORGLIST(responseList[3]?.data?.data);
            }
            toggleLoader(false);
        }).catch((errorList: any) => {
            toggleLoader(false);
            if (errorList?.response?.data?.errors?.length > 0) {
                errorList.response.data.errors.forEach((response: any) => {
                    Toaster.errorToaster(response.message);
                });
            }

        })
    }, [toggleLoader]);

    const initRef = useRef(initializeComponent);
    initRef.current = initializeComponent;
    useEffect(() => {
        initRef.current();
        // Only re-run when user triggers refresh (forceUpdate); avoid loop from callback identity changes
    }, [forceUpdate]);


    const getCurrentOrganizationDetails = () => {
        return getDefaultOrganizations({ "IsDefaultOrg": true })
    }


    //handle Contract creation form
    const handleContractForm = async (values: any, formikHelpers: FormikHelpers<any>) => {
        await props.createOrganizationAndContract(values);
        setForceUpdate((prev) => !prev);
        props.setisVisisblepopup(false);
        formikHelpers.resetForm();
    }


    // Generate Seed

    const handleSeedGenerate = (setFieldValue: any) => {
        // props.toggleLoader(true);
        const secret = "";
        generateSeed(secret).then((res) => {
            props.toggleLoader(false);
            if (res.data?.data?.key && typeof res.data?.data?.key === 'string') {
                setFieldValue("SenderOrgId", currentOrgDetails?.ORGId);
                setFieldValue("Seed", res.data?.data?.key);
            }
        }).catch((error: AxiosError | Error) => {
            props.toggleLoader(false);
        })
    }
    return (
        <>
            <Sharing
                dataSource={contractList}
                columnList={tabColumnList}
                primaryColumn={"_id"}
                showEdit={false}
                showDelete={false}
                showView={false}
                showActions={true}
                keysToHide={['']}
                showUserEdit={true}
                showAdd={true}
                columnAutoWidth={true}
                isOrderIconChange={true}
                fromComponent={'UserComponent'}
                orderIndexedFields={orderIndexedFields}
                onSortingData={onSortingData}
                goToPreviousPage={goToPreviousPage}
                goToNextPage={goToNextPage}
                changePageSize={changePageSize}
                pageSize={pageSize}
                currentPageNo={currentPageNo}
                totalPage={totalPage}
                totalRecord={totalRecord}
                shareData={false}
                createContract={false}
                isLessthanTotalElement={isLessthanTotalElement()}
                viewCredentialApprove={viewCredentialMemoizedApprove}
                viewCredentialApproveData={memoizedFunctionApprove}
                viewCredentialExport={(value) => { }}
                viewCredentialExportData={exportDataJsonHandler}
            />
            <ModalBox
                visible={props.isVisiblePopup}
                onHiding={() => props.setisVisisblepopup(false)}
                showCloseButton={true}
                dragEnabled
                showTitle={true}
                title="Create Contract"
                className="create-contract-data"
                height={'auto'}
                width={500}
                onHidden={() => props.setisVisisblepopup(false)}
                wrapperAttr={{ class: 'create-contract-details' }}
            >
                <Formik
                    initialValues={contractInitialValue}
                    validationSchema={validationSchema}
                    onSubmit={handleContractForm}
                >
                    {({ values, errors, touched, handleChange, handleSubmit, setFieldValue, handleReset, handleBlur }) => (
                        <form onSubmit={handleSubmit} className="dx-form">
                            <SelectWithLabel
                                inputLabel='Receiver Organization Name'
                                stylingMode='outlined'
                                placeholder='Receiver Organization Name'
                                dataSource={allORGList}
                                name={'ReceiverOrgId'}
                                displayExpr="ORGName"
                                valueExpr="ORGId"
                                value={values.ReceiverOrgId}
                                onItemClick={(event) => {
                                    setFieldValue("ReceiverOrgId", event.itemData.ORGId);
                                }}
                                error={touched.ReceiverOrgId && errors.ReceiverOrgId}
                                touched={touched.ReceiverOrgId}
                            />

                            <InputWithLabel
                                inputLabel='Receiver Organization Id'
                                stylingMode='outlined'
                                placeholder='Receiver Organization Id'
                                value={values.ReceiverOrgId}
                                name="ReceiverOrgId"
                                disabled
                                error={touched.ReceiverOrgId && errors.ReceiverOrgId}
                                touched={touched.ReceiverOrgId}
                            />

                            <InputWithLabel
                                inputLabel='Seed'
                                stylingMode='outlined'
                                placeholder="Generate Seed"
                                value={values.Seed}
                                name="Seed"
                                onFocusOut={(e: any) => { handleBlur(e.event) }}
                                onInput={(e: any) => handleChange(e.event)}
                                error={touched.Seed && errors.Seed}
                                touched={touched.Seed}
                                buttons={[
                                    {
                                        name: 'cancel',
                                        location: 'after',
                                        options: {
                                            onClick: () => handleSeedGenerate(setFieldValue),
                                            stylingMode: 'text',
                                            text: 'Generate',
                                            type: 'default'
                                        },
                                    },
                                ]}
                            />

                            <LabelText
                                inputLabel='Sender organization Id'
                                title={currentOrgDetails?.ORGId}
                            />

                            <LabelText
                                inputLabel='Sender organization Name'
                                title={currentOrgDetails?.ORGName}
                            />

                            <div className='contract-btn'>
                                <Button text="Close" className="btn-close" onClick={() => props.setisVisisblepopup(false)} />
                                <Button text="Create" useSubmitBehavior={true} type="default" className="btn-create" onClick={() => handleSubmit} />
                            </div>
                        </form>
                    )}
                </Formik>
            </ModalBox>
        </>
    )
}

const mapStateToProps = (state: any) => ({

});

const mapDispatchToProps = () => (dispatch: any) =>
    bindActionCreators({ toggleLoader: toggleLoader }, dispatch);


export default connect(mapStateToProps, mapDispatchToProps)(ContractTable);


interface IContractTableProps {
    toggleLoader: any;
    activeTab: number;
    isVisiblePopup: boolean;
    setisVisisblepopup: React.SetStateAction<any>;
    initialSelectedOrg: any;
    handleContractSelection: (handleContractSelection: any) => void;
    createOrganizationAndContract: (dataObject: any, contractCase?: string) => Promise<void>;
    searchTextContract: any;
    setSearchTextContract: any;
    forceUpdate: boolean;
}
